import { Box3, Plane, Vector2, Vector3 } from 'three';

export function isPassiveVisual(node) {
    return node.userData?.isPlayerLabel || node.userData?.pickable === false;
}

export function canTapPlacePlayer(item, pitchState) {
    if (item?.type !== 'player') return false;
    const position = pitchState?.[item.side]?.players?.[item.index]?.position;
    return !Array.isArray(position) || position.length < 3;
}

export function sceneItemRoot(object) {
    for (let node = object; node; node = node.parent) {
        if (!node.visible || node.userData?.isShadow || isPassiveVisual(node)) return null;
    }
    for (let node = object; node; node = node.parent) {
        const data = node.userData || {};
        if (data.isZonePoint) {
            let zone = node;
            while (zone && !zone.userData?.isZone && !zone.userData?.isZoneEditor) zone = zone.parent;
            return zone ? { type: 'zonePoint', obj: zone, pointIndex: data.pointIndex } : null;
        }
        if (data.isZone) return { type: 'zone', obj: node };
        if (data.isCone) return { type: 'cone', obj: node };
        if (data.isTarget) return { type: 'target', obj: node };
        if (data.playerInfo) return { type: 'player', obj: node };
        if (data.isBall) return { type: 'ball', obj: node };
    }
    return null;
}

export function itemDescriptor(root) {
    if (!root) return null;
    const data = root.obj.userData;
    if (root.type === 'player') return { type: 'player', side: data.side, index: data.playerIndex };
    const type = root.type === 'zonePoint' ? 'zone' : root.type;
    return ['ball', 'cone', 'target', 'zone'].includes(type) ? { type, id: data[`${type}Id`] } : null;
}

export function itemReference(root) {
    const item = itemDescriptor(root);
    return !item ? null : item.type === 'player' ? `${item.side}:${item.index}` : `${item.type}:${item.id}`;
}

export function firstItemHit(hits, accept = () => true) {
    for (const hit of hits) {
        const root = sceneItemRoot(hit.object);
        if (root && accept(root)) return { root, hit };
    }
    return { root: null, hit: null };
}

// Compare the visible footprint rather than world volume: camera zoom and
// perspective should not make a small object impossible to select.
function projectedBoxArea(box, camera) {
    const corner = new Vector3();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
        corner.set(x, y, z).project(camera);
        minX = Math.min(minX, corner.x); maxX = Math.max(maxX, corner.x);
        minY = Math.min(minY, corner.y); maxY = Math.max(maxY, corner.y);
    }
    return (maxX - minX) * (maxY - minY);
}

// The same projection, in CSS pixels, so a TAP can be measured against it.
function projectedBoxRect(box, camera, width, height) {
    const corner = new Vector3();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
        corner.set(x, y, z).project(camera);
        const px = (corner.x + 1) / 2 * width, py = (1 - corner.y) / 2 * height;
        minX = Math.min(minX, px); maxX = Math.max(maxX, px);
        minY = Math.min(minY, py); maxY = Math.max(maxY, py);
    }
    return { minX, minY, maxX, maxY };
}

// How far a tap lands from that footprint, in pixels — 0 when it is inside it.
function rectGap(px, py, r) {
    const dx = Math.max(r.minX - px, 0, px - r.maxX);
    const dy = Math.max(r.minY - py, 0, py - r.maxY);
    return Math.hypot(dx, dy);
}

// 12px, not more. The strip already answers a thumb over 30 × 24px around an 8px
// key dot — about 11px of slack each way — so this is the SAME generosity the app
// has already chosen, in the place where the objects are smallest on screen. Past
// this, a finger aimed at open turf to orbit the camera would keep grabbing a
// neighbour, and dragging the wrong item is worse than missing the right one.
export const TOUCH_PICK_PX = 12;

// One coordinate and raycast path for taps, dragging, linking and both cameras.
export function createViewportPicker({ getCanvas, getCamera, getScene, raycaster }) {
    const mouse = new Vector2(), ground = new Plane(new Vector3(0, 1, 0), 0);
    return (event, accept = () => true) => {
        const canvas = getCanvas(), camera = getCamera(), scene = getScene();
        const empty = { root: null, hit: null, hits: [], ground: null };
        if (!canvas || !camera || !scene) return empty;
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return empty;
        // OrbitControls changes transforms between renders. Picking must use
        // the current camera and rebuilt/animated meshes, never the last frame.
        camera.updateMatrixWorld(true);
        scene.updateMatrixWorld(true);
        mouse.set((event.clientX - rect.left) / rect.width * 2 - 1,
            -(event.clientY - rect.top) / rect.height * 2 + 1);
        raycaster.setFromCamera(mouse, camera);
        // Solid items use one world-space box assembled from their visible
        // meshes. Labels, shadows and nested editor handles never enlarge it.
        const boxes = new Map(), hits = [], meshBox = new Box3();
        scene.traverse(node => {
            const root = sceneItemRoot(node);
            if (!root) return;
            if (['player', 'ball', 'cone', 'target'].includes(root.type)) {
                if (!node.isMesh || !node.geometry) return;
                let bounds;
                if (node.isSkinnedMesh) {
                    node.computeBoundingBox(); // use the current animation pose
                    bounds = node.boundingBox;
                } else {
                    if (!node.geometry.boundingBox) node.geometry.computeBoundingBox();
                    bounds = node.geometry.boundingBox;
                }
                if (!bounds || bounds.isEmpty()) return;
                meshBox.copy(bounds).applyMatrix4(node.matrixWorld);
                if (!boxes.has(root.obj)) boxes.set(root.obj, { root, box: new Box3() });
                boxes.get(root.obj).box.union(meshBox);
            } else {
                // Zones and their edit handles keep their precise shape.
                hits.push(...raycaster.intersectObject(node, false).map(hit => ({ ...hit,
                    pickArea: root.type === 'zonePoint' ? -1 : Infinity })));
            }
        });
        for (const { root, box } of boxes.values()) {
            const point = raycaster.ray.intersectBox(box, new Vector3());
            if (!point) continue;
            const distance = raycaster.ray.origin.distanceTo(point);
            if (distance < raycaster.near || distance > raycaster.far) continue;
            hits.push({ object: root.obj, point, distance, pickArea: projectedBoxArea(box, camera) });
        }
        // All boxes under this touch compete by screen size; equal sizes use depth.
        hits.sort((a, b) => (a.pickArea - b.pickArea) || a.distance - b.distance);
        const exact = firstItemHit(hits, accept);
        // A MEASUREMENT, not a decision: how far the nearest acceptable item's own
        // screen footprint sits from this tap, in pixels. The caller decides whether
        // a near-miss may become a grab — selecting and dragging want the item the
        // finger aimed at; placing a new one, or grabbing a zone vertex or a bend
        // handle, wants exactly what the ray says. Touch only: a mouse pointer is a
        // pixel wide and its misses are deliberate.
        let near = null, nearPx = Infinity;
        if (!exact.root && event.pointerType === 'touch') {
            const tapX = event.clientX - rect.left, tapY = event.clientY - rect.top;
            for (const { root, box } of boxes.values()) {
                if (!accept(root)) continue;
                const gap = rectGap(tapX, tapY, projectedBoxRect(box, camera, rect.width, rect.height));
                if (gap <= TOUCH_PICK_PX && gap < nearPx) { nearPx = gap; near = root; }
            }
        }
        return { ...exact, near, hits,
            ground: raycaster.ray.intersectPlane(ground, new Vector3()) };
    };
}

export class PointerGesture {
    constructor(threshold = 6) { this.threshold = threshold; this.session = null; }
    begin(event, root) {
        if (this.session) {
            if (this.session.owner === 'item' || root) return { blocked: true };
            this.session.pointers.add(event.pointerId);
            this.session.dragged = true; // pinch never turns into a selection tap
            return { added: true, session: this.session };
        }
        this.session = { x: event.clientX, y: event.clientY, pointerId: event.pointerId,
            active: true, dragged: false, root, itemOwned: !!root, owner: root ? 'item' : 'camera',
            pointers: new Set([event.pointerId]) };
        return { started: true, session: this.session };
    }
    move(event) {
        const session = this.session;
        if (!session?.pointers.has(event.pointerId)) return null;
        if (Math.hypot(event.clientX - session.x, event.clientY - session.y) > this.threshold) session.dragged = true;
        return session;
    }
    end(event, cancelled = false) {
        const session = this.session;
        if (!session?.pointers.has(event.pointerId)) return null;
        this.move(event);
        session.pointers.delete(event.pointerId);
        if (session.pointers.size) {
            session.pointerId = session.pointers.values().next().value;
            session.dragged = true;
            return null;
        }
        this.session = null;
        session.cancelled = cancelled;
        return session;
    }
    reset() { const session = this.session; this.session = null; return session; }
}

// Observe termination in capture phase, before camera controls release capture.
// Item events never enter OrbitControls; camera events keep their normal flow.
export function bindViewportPointers({ canvas, pick, gesture, onStart, onMove, onEnd, onCancel }) {
    const win = canvas.ownerDocument.defaultView;
    const blockedPointers = new Set();
    const stop = event => { event.preventDefault(); event.stopImmediatePropagation(); };
    const notifyCameraCancel = pointerId => {
        const event = new win.Event('pointercancel', { bubbles: true });
        Object.defineProperty(event, 'pointerId', { value: pointerId });
        canvas.dispatchEvent(event);
    };
    const down = event => {
        const result = gesture.begin(event, pick(event).root);
        if (result.blocked) { blockedPointers.add(event.pointerId); stop(event); return; }
        if (!result.started) return;
        if (result.session.itemOwned) {
            stop(event);
            canvas.setPointerCapture(event.pointerId);
        }
        onStart(event, result.session);
    };
    const move = event => {
        if (blockedPointers.has(event.pointerId)) { stop(event); return; }
        const session = gesture.move(event);
        if (!session) return;
        if (session.itemOwned) stop(event);
        onMove(event, session);
    };
    const finish = (event, cancelled = false, lostCapture = false) => {
        if (blockedPointers.delete(event.pointerId)) { stop(event); return; }
        const tracked = gesture.session?.pointers.has(event.pointerId);
        const owner = gesture.session?.owner;
        const session = gesture.end(event, cancelled);
        if (lostCapture && tracked && owner === 'camera') notifyCameraCancel(event.pointerId);
        if (!session) return;
        if (session.itemOwned && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
        if (cancelled) onCancel(event, session); else onEnd(event, session);
    };
    const up = event => finish(event);
    const cancel = event => finish(event, true);
    const lost = event => finish(event, true, true);
    const blur = () => {
        const session = gesture.reset();
        blockedPointers.clear();
        if (!session) return;
        onCancel({ pointerId: session.pointerId }, session);
        // Give OrbitControls a cancellation for every camera pointer it saw.
        for (const pointerId of session.pointers) {
            if (session.owner === 'camera') notifyCameraCancel(pointerId);
            if (canvas.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId);
        }
    };
    const visibility = () => { if (canvas.ownerDocument.hidden) blur(); };
    canvas.addEventListener('pointerdown', down, true);
    canvas.addEventListener('lostpointercapture', lost);
    win.addEventListener('pointermove', move, true);
    win.addEventListener('pointerup', up, true);
    win.addEventListener('pointercancel', cancel, true);
    win.addEventListener('blur', blur);
    canvas.ownerDocument.addEventListener('visibilitychange', visibility);
    return () => {
        canvas.removeEventListener('pointerdown', down, true);
        canvas.removeEventListener('lostpointercapture', lost);
        win.removeEventListener('pointermove', move, true);
        win.removeEventListener('pointerup', up, true);
        win.removeEventListener('pointercancel', cancel, true);
        win.removeEventListener('blur', blur);
        canvas.ownerDocument.removeEventListener('visibilitychange', visibility);
        blur();
    };
}
